import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  DocumentTextIcon,
  ChartBarIcon,
  UserIcon,
  Cog6ToothIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import NotificationDropdown from '../notifications/NotificationDropdown';

const MainLayout = () => {
  const { currentUser, logout } = useAuth();
  // L'intérimaire garde son propre menu : l'intérim ouvre la validation à l'étape
  // directrice, pas les rapports ni l'administration (le serveur les lui refuse).
  const effectiveRole = currentUser?.role;
  const { unreadCount } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);

  // Navigation items based on user role
  const navigationItems = [
    { name: 'Tableau de bord', path: '/dashboard', icon: HomeIcon },
    { name: 'Demandes', path: '/requests', icon: DocumentTextIcon },
    {
      name: 'Nouvelle demande BU',
      path: '/requests/create-business-unit',
      icon: PlusCircleIcon,
      roles: ['business_unit'],
    },
    {
      name: 'Récap Décharges',
      path: '/requests/discharge-recap',
      icon: DocumentTextIcon,
      roles: ['force_de_vente', 'chef_de_zone', 'directrice_marketing'],
    },
    {
      name: 'Rapport Supply Chain',
      path: '/reports/supply-chain',
      icon: ChartBarIcon,
      roles: ['commercial', 'directrice_marketing'],
    },
    {
      name: 'Statistiques',
      path: '/statistics',
      icon: ChartBarIcon,
      roles: ['marketing', 'force_de_vente', 'commercial', 'directrice_marketing', 'chef_de_zone', 'animateur', 'business_unit'],
    },
    // Le profil s'ouvre depuis le bloc nom/poste en bas de la barre : pas d'entrée ici.
  ];

  // Admin navigation items
  const adminItems = [
    { name: 'Gestion des utilisateurs', path: '/admin/users', icon: Cog6ToothIcon },
    { name: 'Gestion des produits', path: '/admin/products', icon: Cog6ToothIcon },
    { name: 'Gestion des distributeurs', path: '/admin/distributors', icon: Cog6ToothIcon },
    { name: 'Gestion des sites internes', path: '/admin/internal-sites', icon: Cog6ToothIcon },
    // La gestion des zones appartient à la directrice, pas aux collaborateurs marketing.
    { name: 'Gestion des Zones', path: '/marketing/zones', icon: Cog6ToothIcon },
  ];

  // Chef de Zone navigation items
  const chefDeZoneItems = [
    { name: 'Mon Dashboard', path: '/chef-de-zone/dashboard', icon: Cog6ToothIcon },
  ];

  // Animateur navigation items
  const animateurItems = [
    { name: 'Demandes à prendre en charge', path: '/animateur/dashboard', icon: DocumentTextIcon },
  ];

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col flex-grow glassmorphism border-r border-gray-200 pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <img
                className="h-12 w-auto"
                src="/assets/logo.webp"
                alt="Tchin-Lait"
              />
            </div>
            <div className="mt-5 flex-grow flex flex-col">
              <nav className="flex-1 px-2 space-y-1">
                {navigationItems.filter(item => !item.roles || item.roles.includes(effectiveRole)).map((item) => (
                  <div key={item.name} className="flex items-center">
                    <Link
                      to={item.path}
                      className={`${
                        location.pathname === item.path
                          ? 'bg-primary-100 text-primary-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      } group flex items-center flex-1 px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150`}
                    >
                      <item.icon
                        className={`${
                          location.pathname === item.path
                            ? 'text-primary-500'
                            : 'text-gray-400 group-hover:text-gray-500'
                        } mr-3 flex-shrink-0 h-6 w-6 transition-colors duration-150`}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                    {item.path === '/requests' && ['animateur', 'chef_de_zone', 'marketing'].includes(currentUser?.role) && (
                      <Link
                        to="/requests/create-animateur"
                        title="Créer une demande"
                        className="ml-1 p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 cursor-pointer transition-colors duration-150"
                      >
                        <PlusCircleIcon className="h-5 w-5" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                ))}

                {/* Admin navigation items */}
                {currentUser && currentUser.role === 'directrice_marketing' && (
                  <div className="pt-2 mt-2 border-t border-gray-200">
                    <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Administration
                    </h3>
                    <div className="mt-1 space-y-1">
                      {adminItems.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`${
                            location.pathname === item.path
                              ? 'bg-primary-100 text-primary-900'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150`}
                        >
                          <item.icon
                            className={`${
                              location.pathname === item.path
                                ? 'text-primary-500'
                                : 'text-gray-400 group-hover:text-gray-500'
                            } mr-3 flex-shrink-0 h-6 w-6 transition-colors duration-150`}
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Chef de Zone navigation items */}
                {currentUser && currentUser.role === 'chef_de_zone' && (
                  <div className="pt-2 mt-2 border-t border-gray-200">
                    <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Ma Zone
                    </h3>
                    <div className="mt-1 space-y-1">
                      {chefDeZoneItems.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`${
                            location.pathname === item.path
                              ? 'bg-primary-100 text-primary-900'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150`}
                        >
                          <item.icon
                            className={`${
                              location.pathname === item.path
                                ? 'text-primary-500'
                                : 'text-gray-400 group-hover:text-gray-500'
                            } mr-3 flex-shrink-0 h-6 w-6 transition-colors duration-150`}
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Distributeur navigation items */}
                {currentUser && currentUser.role === 'distributeur' && (
                  <div className="pt-2 mt-2 border-t border-gray-200">
                    <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Mes décharges
                    </h3>
                    <div className="mt-1 space-y-1">
                      <Link
                        to="/distributeur/dashboard"
                        className={`${
                          location.pathname === '/distributeur/dashboard'
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150`}
                      >
                        <DocumentTextIcon
                          className={`${
                            location.pathname === '/distributeur/dashboard'
                              ? 'text-primary-500'
                              : 'text-gray-400 group-hover:text-gray-500'
                          } mr-3 flex-shrink-0 h-6 w-6 transition-colors duration-150`}
                          aria-hidden="true"
                        />
                        Décharges à signer
                      </Link>
                    </div>
                  </div>
                )}

                {/* Animateur navigation items */}
                {currentUser && currentUser.role === 'animateur' && (
                  <div className="pt-2 mt-2 border-t border-gray-200">
                    <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Mes demandes
                    </h3>
                    <div className="mt-1 space-y-1">
                      {animateurItems.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`${
                            location.pathname === item.path
                              ? 'bg-primary-100 text-primary-900'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150`}
                        >
                          <item.icon
                            className={`${
                              location.pathname === item.path
                                ? 'text-primary-500'
                                : 'text-gray-400 group-hover:text-gray-500'
                            } mr-3 flex-shrink-0 h-6 w-6 transition-colors duration-150`}
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </nav>
            </div>
            {/* Le bloc nom/poste EST le bouton « Mon profil » (pas d'entrée en double dans le menu). */}
            <Link
              to="/profile"
              className={`flex-shrink-0 flex items-center border-t border-gray-200 p-4 hover:bg-gray-50 ${
                location.pathname === '/profile' ? 'bg-gray-100' : ''
              }`}
            >
              <div className="flex-shrink-0">
                <UserIcon className="inline-block h-9 w-9 rounded-full text-gray-500" />
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-700 truncate">
                  {currentUser?.name}
                </p>
                <p className="text-xs font-medium text-gray-500">
                  {currentUser?.department}
                </p>
                {currentUser?.actingInterim && (
                  <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                    Directeur par intérim
                  </span>
                )}
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="relative z-10 flex-shrink-0 flex h-16 glassmorphism shadow">
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              <h1 className="text-2xl font-semibold text-gray-800">
                {navigationItems.find(item => item.path === location.pathname)?.name ||
                 adminItems.find(item => item.path === location.pathname)?.name ||
                 chefDeZoneItems.find(item => item.path === location.pathname)?.name ||
                 animateurItems.find(item => item.path === location.pathname)?.name ||
                 'Tchin-Lait'}
              </h1>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              {/* Notification button */}
              <div className="relative">
                <button
                  type="button"
                  className="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={toggleNotifications}
                >
                  <span className="sr-only">Voir les notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                </button>
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 block h-5 w-5 rounded-full bg-red-500 text-white text-xs font-medium flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
                {showNotifications && (
                  <NotificationDropdown onClose={() => setShowNotifications(false)} />
                )}
              </div>

              {/* Logout button */}
              <button
                type="button"
                className="ml-3 bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                onClick={handleLogout}
              >
                <span className="sr-only">Se déconnecter</span>
                <ArrowRightOnRectangleIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
